<template>
  <main v-if="meldeblatt != null" class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ $t('pageMeldeblatt.title') }}
    </portal>

    <card :title="$t('pageMeldeblatt.card.general')" cols="12 6@desktop" rows="2">
      <hm-card-inner v-if="meldeblatt.dateMismatch || meldeblatt.numPersonsMismatch || meldeblatt.anotherMeldeblattAlreadyExists" :full-size="true">
        <hm-col cols="12">
          <span v-if="meldeblatt.dateMismatch" class="warning-text warning-text--full-size warning-text--no-margin col-12"><b>{{ $t('pageMeldeblatt.warning') }}:</b> {{ $t('pageMeldeblatt.warningDateMismatch') }}</span>
          <span v-if="meldeblatt.numPersonsMismatch" class="warning-text warning-text--full-size warning-text--no-margin col-12"><b>{{ $t('pageMeldeblatt.warning') }}:</b> {{ $t('pageMeldeblatt.warningNumPersonMismatch') }}</span>
          <span v-if="meldeblatt.anotherMeldeblattAlreadyExists" class="warning-text warning-text--full-size warning-text--no-margin col-12"><b>{{ $t('pageMeldeblatt.warning') }}:</b> {{ $t('pageMeldeblatt.warningAnotherMeldeblattIsArrived') }}</span>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner :title="$t('pageMeldeblatt.card.statusDate')">
        <hm-input-select v-model="meldeblatt.meldeblatt.status"
                         :disabled="true"
                         :label="$t('pageMeldeblatt.status')"
                         name="status"
                         enum-name="MeldeStatus"
                         cols="12 6@tablet 4@desktop" />
        <hm-input v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                  :value="$t(`MeldeblattValidationStatus.${meldeblatt.meldeblatt.validationStatus}`)"
                  :disabled="true"
                  :label="$t('pageMeldeblatt.validationStatus')"
                  name="validationStatus"
                  cols="12 6@tablet 4@desktop" />
        <hm-input v-if="meldeblatt.meldeblatt.meldeblattNummer > 0"
                  :value="meldeblatt.meldeblatt.meldeblattNummer"
                  :disabled="true"
                  :label="$t('pageMeldeblatt.number')"
                  name="number"
                  cols="12 6@tablet 4@desktop" />
        <hm-input-select v-model="meldeblatt.meldeblatt.typ"
                         :disabled="!meldeblatt.editable || ['Sent', 'Printed'].includes(meldeblatt.meldeblatt.status)"
                         :label="$t('pageMeldeblatt.type')"
                         :disabled-options="['AVS'].includes(config.typ) ? ['GroupDetailed'] : []"
                         name="type"
                         enum-name="Meldetyp"
                         cols="12 6@tablet 4@desktop" />
        <hm-input-select id="configurationId"
                         :disabled="meldeblatt.meldeblatt.status !== 'Draft'"
                         v-model="meldeblatt.meldeblatt.configurationId"
                         :label="$t('pageMeldeblatt.configurationId')"
                         name="configurationId"
                         cols="12 6@tablet 4@desktop" >
          <option v-for="itConfig in configurations" :value="itConfig.id" :disabled="!itConfig.enabled">{{ itConfig.name }} ({{ $t(`MeldeclientTyp.${itConfig.typ}`) }})</option>
        </hm-input-select>
        <hm-input v-if="!isEmpty(meldeblatt.meldeblattURL)"
                  :value="meldeblatt.meldeblattURL"
                  :disabled="true"
                  :label="$t('pageMeldeblatt.meldeblattURL')"
                  cols="12 6@tablet 4@desktop">
          <template slot="icon-inline-right" slot-scope="slot">
            <input-icon-button :border="true" :title="$t('pageMeldeblatt.copyToClipboard')" icon="copy" @click="$copy(meldeblatt.meldeblattURL)" />
          </template>
        </hm-input>
        <hm-input-date v-model="meldeblatt.meldeblatt.anreise"
                       :disabled="!meldeblatt.editable"
                       :label="$t('pageMeldeblatt.arrivalDate')"
                       cols="12 6@tablet 4@desktop start-1"
                       name="arrivalDate" />
        <hm-input-date v-model="meldeblatt.meldeblatt.abreiseGeplant"
                       :min-date="meldeblatt.meldeblatt.anreise"
                       :min-date-inclusive="true"
                       :disabled="!meldeblatt.editable || ['Sent', 'Printed'].includes(meldeblatt.meldeblatt.status)"
                       :label="$t('pageMeldeblatt.departureDatePlanned')"
                       cols="12 6@tablet 4@desktop"
                       name="departureDate" />
        <hm-input-date v-if="['Sent', 'Printed', 'Departed'].includes(meldeblatt.meldeblatt.status)"
                       v-model="meldeblatt.meldeblatt.abreise"
                       :min-date="meldeblatt.meldeblatt.anreise"
                       :min-date-inclusive="true"
                       :disabled="meldeblatt.meldeblatt.status === 'Departed'"
                       :label="$t('pageMeldeblatt.departureDate')"
                       cols="12 6@tablet 4@desktop"
                       name="departureDate" />
      </hm-card-inner>
      <hm-card-inner :title="$t('pageMeldeblatt.card.meldeblatt')">
        <hm-input-number v-model="meldeblatt.meldeblatt.aufenthalte"
                         :disabled="!meldeblatt.editable"
                         :label="$t('pageMeldeblatt.aufenthalte')"
                         :help="$t('pageMeldeblatt.aufenthalte_help')"
                         cols="12 6@tablet 4@desktop start-1"
                         name="aufenthalte" />
        <hm-input v-model="meldeblatt.meldeblatt.herreiseVon"
                  :disabled="!meldeblatt.editable"
                  :label="$t('pageMeldeblatt.herreiseVon')"
                  cols="12 6@tablet 4@desktop"
                  name="herreiseVon" />
        <hm-input v-model="meldeblatt.meldeblatt.reiseziel"
                  :disabled="!meldeblatt.editable"
                  :label="$t('pageMeldeblatt.reiseziel')"
                  cols="12 6@tablet 4@desktop"
                  name="reiseziel" />
        <hm-input v-model="meldeblatt.meldeblatt.kfzKennzeichen"
                  :disabled="!meldeblatt.editable"
                  :label="$t('pageMeldeblatt.kfzKennzeichen')"
                  cols="12 6@tablet 4@desktop"
                  name="kfzKennzeichen" />
      </hm-card-inner>
      <hm-card-inner v-if="['AVS'].includes(config.typ) && !isEmpty(meldeblatt.meldeblatt.additionalFields)" title="AVS">
        <hm-input v-if="!isNil(meldeblatt.meldeblatt.additionalFields['AVS_BETRAG'])"
                  :value="meldeblatt.meldeblatt.additionalFields['AVS_BETRAG']"
                  cols="12 6@tablet 4@desktop"
                  name="AVS_BETRAG"
                  label="AVS Betrag"
                  disabled />
        <hm-input v-if="!isNil(meldeblatt.meldeblatt.additionalFields['AVS_ABRECHNUNGSSTATUS'])"
                  :value="meldeblatt.meldeblatt.additionalFields['AVS_ABRECHNUNGSSTATUS']"
                  cols="12 6@tablet 4@desktop"
                  name="AVS_ABRECHNUNGSSTATUS"
                  label="AVS Abrechnungsstatus"
                  disabled />
      </hm-card-inner>
      <hm-card-inner v-if="meldeblatt.meldeblatt.signatureBase64Image" :deletable="['Draft','Reservation'].includes(meldeblatt.meldeblatt.status)" :title="$t('pageMeldeblatt.signature')" @delete="deleteSignature">
        <hm-col cols="12 8@desktop start-1" class="hm-input__image-wrapper">
          <img :src="meldeblatt.meldeblatt.signatureBase64Image" class="hm-input__image">
        </hm-col>
        <hm-input v-if="!isEmpty(meldeblatt.meldeblatt.signatureDateTimeUtc)"
                  :disabled="true"
                  :label="$t('pageMeldeblatt.signatureDateTime')"
                  :value="meldeblatt.meldeblatt.signatureDateTimeUtc | toLocalDateTime"
                  cols="12 8@desktop start-1"
                  name="managedBy" />
      </hm-card-inner>
    </card>
    <hm-col cols="12 6@desktop">
      <paginated-table :items-per-page="5"
                       :rows="meldeblatt.roomReservations"
                       :sticky="true"
                       min-width="600px"
                       columns="8 5@mobile">
        <template slot="table-head">
          <div class="hm-table__th">{{ $t('pageMeldeblatt.reservations.reservationNumber') }}</div>
          <div class="hm-table__th">{{ $t('pageMeldeblatt.reservations.name') }}</div>
          <div class="hm-table__th">{{ $t('pageMeldeblatt.reservations.arrivalDate') }}</div>
          <div class="hm-table__th">{{ $t('pageMeldeblatt.reservations.departureDate') }}</div>
          <div class="hm-table__th hidden@mobile">{{ $t('pageMeldeblatt.reservations.numAdults') }}</div>
          <div class="hm-table__th hidden@mobile">{{ $t('pageMeldeblatt.reservations.numChildren') }}</div>
          <div class="hm-table__th hidden@mobile">{{ $t('pageMeldeblatt.reservations.reservationStatus') }}</div>
          <div class="hm-table__th">{{ $t('pageMeldeblatt.reservations.roomNumber') }}</div>
        </template>
        <template v-slot:default="slotProps">
          <template v-for="row in slotProps.currentPage">
            <div class="hm-table__td" @click="openReservation(row.reservationId)">{{ row.reservationNumber }}</div>
            <div class="hm-table__td privacy--blur" @click="openReservation(row.reservationId)">{{ row.stayDetails.displayName }}</div>
            <div class="hm-table__td" @click="openReservation(row.reservationId)">{{ row.checkInDate | moment($moment.localeData().longDateFormat('L')) }}</div>
            <div class="hm-table__td" @click="openReservation(row.reservationId)">{{ row.checkOutDate | moment($moment.localeData().longDateFormat('L')) }}</div>
            <div class="hm-table__td hidden@mobile" @click="openReservation(row.reservationId)">{{ row.stayDetails.numAdults }}</div>
            <div class="hm-table__td hidden@mobile" @click="openReservation(row.reservationId)">{{ row.stayDetails.numChildren }}</div>
            <div class="hm-table__td hidden@mobile" @click="openReservation(row.reservationId)">{{ $t(`ReservationStatus.${row.status}`) }}</div>
            <div class="hm-table__td" @click="openReservation(row.reservationId)">{{ row.stayDetails.roomNumber }}</div>
          </template>
        </template>
      </paginated-table>
    </hm-col>
    <card v-if="meldeblatt.meldeblatt.typ === 'GroupSimplified'" :title="$t('pageMeldeblatt.card.travelGroup')" cols="12 6@desktop" rows="1 3@desktop">
      <hm-card-inner>
        <hm-col cols="12">
          <div style="position:relative; margin-bottom: 0.8rem;">{{ $t('pageMeldeblatt.travelGroupHint') }}</div>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner :title="$t('pageMeldeblatt.card.gastart')">
        <hm-col :grid="true" cols="12">
          <template v-for="gastart in config.gastarten">
            <hm-input :value="`${gastart.name[hotelSettings.details.primaryLanguage]} (${gastart.ageFrom} - ${gastart.ageUntil})`"
                      :disabled="true"
                      :key="`gastart_${gastart.code}`"
                      :label="$t('pageMeldeblatt.gastart')"
                      cols="8 6@tablet" />
            <hm-input-select ref="userInputRefs"
                             v-model="meldeblatt.meldeblatt.userInputGastart[gastart.code]"
                             :disabled="!meldeblatt.editable"
                             :key="`gastart_input_${gastart.code}`"
                             :label="$t('pageMeldeblatt.numGastart')"
                             :parent-errors="errors"
                             validate="userInputCountsMatch"
                             name="numGastart"
                             cols="4 6@tablet"
                             @input="updateUserInputValidations">
              <option v-for="item in intArray(100)" :value="item" v-html="item"></option>
            </hm-input-select>
          </template>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner v-if="!['AVS'].includes(config.typ)" :title="$t('pageMeldeblatt.card.originCountries')">
        <hm-col :grid="true" cols="12">
          <template v-for="(landschl, idx) in meldeblatt.meldeblatt.userInputLandschl">
            <hm-select v-model="landschl[0]"
                       :options="getI18nCountries($shortLocale)"
                       :disabled="!meldeblatt.editable"
                       :key="`landschl_${idx}`"
                       :clearable="false"
                       :label="$t('pageMeldeblatt.country')"
                       name="country"
                       cols="8 6@tablet" />
            <hm-input-select ref="userInputRefs"
                             v-model="landschl[1]"
                             :disabled="!meldeblatt.editable"
                             :key="`landschl_input_${idx}`"
                             :label="$t('pageMeldeblatt.numGastart')"
                             :parent-errors="errors"
                             validate="userInputCountsMatch"
                             name="numGastart"
                             cols="4 6@tablet"
                             @input="updateUserInputValidations">
              <option v-for="item in intArray(100)" :value="item" v-html="item"></option>
            </hm-input-select>
          </template>
        </hm-col>
      </hm-card-inner>
      <hm-card-inner v-if="meldeblatt.meldeblatt.userInputLandschl.length < 6">
        <hm-button v-if="meldeblatt.meldeblatt.userInputLandschl.length < 6"
                   class="btn--blue btn--no-margin"
                   cols="12 6@tablet 3@desktop 2@hd start-1 start-7@tablet start-10@desktop start-11@hd"
                   icon-left="plus"
                   @click="meldeblatt.meldeblatt.userInputLandschl.push([hotelSettings.details.countryCode, 0])">{{ $t('pageMeldeblatt.actionAddOriginCountry') }}</hm-button>
      </hm-card-inner>
    </card>

    <pagination ref="guestPagination" :all-rows="meldeblatt.meldeblatt.guests" :items-per-page="6" :cols="(['AVS'].includes(config.typ) && meldeblatt.meldeblatt.typ === 'GroupSimplified') ? '12 6@hd' : '12'">
      <template v-slot:default="slotProps">
        <template v-for="(guest, idx) in slotProps.currentPage">
          <card :title="$t('pageMeldeblatt.card.guest', { idx: (idx + (slotProps.pageNumber-1) * 6 + 1) })"
                :key="idx + (slotProps.pageNumber-1) * 6"
                :card-type="meldeblatt.meldeblatt.guests.length > 1 && meldeblatt.editable && (idx !== 0 || slotProps.pageNumber !== 1)? 'record' : 'default'"
                :cols="(['AVS'].includes(config.typ) && meldeblatt.meldeblatt.typ === 'GroupSimplified') ? '12' : '12 6@hd'"
                @delete="deleteGuest(guest)">
            <hm-card-inner v-if="['AVS'].includes(config.typ) && meldeblatt.meldeblatt.typ === 'GroupSimplified' && (idx > 0 || slotProps.pageNumber > 1)" :full-size="true">
              <hm-col cols="12">
                <span class="error-text error-text--full-size error-text--no-margin col-12">{{ $t('pageMeldeblatt.avsNoAdditionalPersonsForGroup') }}</span>
              </hm-col>
            </hm-card-inner>
            <hm-card-inner>
              <hm-input v-model="guest.namePrefix"
                        :disabled="!meldeblatt.editable"
                        :label="$t('pageMeldeblatt.namePrefix')"
                        cols="12 6@tablet 3@desktop 3@hd"
                        name="namePrefix" />
              <hm-input v-model="guest.firstName"
                        :disabled="!meldeblatt.editable"
                        :label="$t('pageMeldeblatt.firstName')"
                        cols="12 6@tablet 3@desktop 3@hd"
                        name="firstName" />
              <hm-input v-model="guest.lastName"
                        :disabled="!meldeblatt.editable || (meldeblatt.meldeblatt.status === 'Reservation' && idx === 0 && slotProps.pageNumber === 1)"
                        :label="$t('pageMeldeblatt.lastName')"
                        :parent-errors="errors"
                        :name="`meldeblatt.meldeblatt.guests.\$each[${slotProps.baseIndex + idx}].lastName`"
                        cols="12 6@tablet 3@desktop 3@hd" />
              <hm-input-select v-model="guest.gasttyp"
                               :disabled="!meldeblatt.editable || (idx === 0 && slotProps.pageNumber === 1)"
                               :label="$t('pageMeldeblatt.gasttyp')"
                               :disabled-options="['HauptgemelderGast']"
                               cols="12 6@tablet 3@desktop 3@hd"
                               name="gasttyp"
                               enum-name="GuestType" />
              <hm-input-date v-model="guest.dateOfBirth"
                             :disabled="!dateOfBirthEditable[slotProps.baseIndex + idx]"
                             :label="$t('pageMeldeblatt.dateOfBirth')"
                             :parent-errors="errors"
                             :max-date="today"
                             :name="`meldeblatt.meldeblatt.guests.\$each[${slotProps.baseIndex + idx}].dateOfBirth`"
                             min-date="19041231"
                             cols="12 6@tablet 3@desktop 3@hd start-1"
                             @input="updateGastart(guest, slotProps.baseIndex + idx)" />
              <hm-select v-model="guest.nationality"
                         :options="getI18nCountries($shortLocale, [guest.nationality, guest.address.country, ...meldeblatt.meldeblatt.guests.map(it => it.nationality), ...meldeblatt.meldeblatt.guests.map(it => isNil(it.address) ? null : it.address.country)])"
                         :disabled="!meldeblatt.editable"
                         :label="$t('pageMeldeblatt.nationality')"
                         name="nationality"
                         cols="12 6@tablet 3@desktop 3@hd" />
              <hm-gender v-model="guest.gender"
                         :disabled="!genderEditable[slotProps.baseIndex + idx]"
                         :label="$t('pageMeldeblatt.gender')"
                         name="gender"
                         cols="12 6@tablet 3@desktop 3@hd" />
              <hm-input-select v-model="guest.gastart"
                               :disabled="!gastartEditable[slotProps.baseIndex + idx]"
                               :label="$t('pageMeldeblatt.gastart')"
                               :parent-errors="errors"
                               :name="`meldeblatt.meldeblatt.guests.\$each[${slotProps.baseIndex + idx}].gastart`"
                               cols="12 6@tablet 3@desktop 3@hd">
                <option v-for="item in config.gastarten" :disabled="(idx > 0 || slotProps.pageNumber > 1) && item.primaryGuestOnly" :value="item.code">{{ item.name[hotelSettings.details.primaryLanguage] }} ({{ item.ageFrom }} - {{ item.ageUntil }})</option>
              </hm-input-select>
              <hm-input v-model="guest.email.emailAddress"
                        :disabled="!meldeblatt.editable"
                        :label="$t('pageMeldeblatt.emailAddress')"
                        cols="12 6@tablet 3@desktop 3@hd"
                        name="emailAddress" />
              <hm-input-toggle v-if="(idx + (slotProps.pageNumber-1) * 6) > 0"
                               v-model="guest.sameAddressAsPrimary"
                               :disabled="!meldeblatt.editable"
                               :label="$t('pageMeldeblatt.sameAddressAsPrimary')"
                               :enabled-title="$t('pageMeldeblatt.sameAddressAsPrimaryEnabled')"
                               :disabled-title="$t('pageMeldeblatt.sameAddressAsPrimaryDisabled')"
                               cols="12 6@tablet 3@desktop 3@hd" />
              <hm-input v-if="['Deskline3VTPMS'].includes(config.typ)"
                        v-model="guest.zusatztext"
                        :disabled="!zusatztextEditable[slotProps.baseIndex + idx]"
                        :label="$t('pageMeldeblatt.zusatztext')"
                        :help="$t('pageMeldeblatt.zusatztext_help')"
                        cols="12 6@tablet 3@desktop 3@hd"
                        name="zusatztext" />
              <hm-input v-if="['AVS'].includes(config.typ) && !isEmpty(guest.additionalFields) && !isEmpty(guest.additionalFields['AVS_PERSONID'])"
                        :value="guest.additionalFields['AVS_PERSONID']"
                        :disabled="true"
                        label="AVS ID"
                        cols="12 6@tablet 3@desktop 3@hd"
                        name="AVS_PERSONID" />
            </hm-card-inner>
            <hm-card-inner :title="$t('pageMeldeblatt.card.travelIdDocument')">
              <hm-input-select v-model="guest.document.type"
                               :disabled="!meldeblatt.editable"
                               :label="$t('pageMeldeblatt.documentType')"
                               :parent-errors="errors"
                               :name="`meldeblatt.meldeblatt.guests[${slotProps.baseIndex + idx}].travelDocumentType`"
                               enum-name="TravelDocumentType"
                               cols="12 6@tablet 3@desktop 3@hd" />
              <hm-input v-model="guest.document.number"
                        :disabled="!meldeblatt.editable"
                        :label="$t('pageMeldeblatt.documentNumber')"
                        :parent-errors="errors"
                        :name="`meldeblatt.meldeblatt.guests[${slotProps.baseIndex + idx}].travelDocumentNumber`"
                        cols="12 6@tablet 3@desktop 3@hd"/>
              <hm-input v-model="guest.document.issuingAuthority"
                        :disabled="!meldeblatt.editable"
                        :label="$t('pageMeldeblatt.issuingAuthority')"
                        cols="12 6@tablet 3@desktop 3@hd"
                        name="issuingAuthority" />
              <hm-input-date v-model="guest.document.issueDate"
                             :disabled="!meldeblatt.editable"
                             :label="$t('pageMeldeblatt.issueDate')"
                             cols="12 6@tablet 3@desktop 3@hd"
                             name="issueDate" />
            </hm-card-inner>
            <hm-card-inner v-if="guest.sameAddressAsPrimary === false || 0 === (idx + (slotProps.pageNumber-1) * 6)" :title="$t('pageMeldeblatt.card.primaryAddress')">
              <hm-textarea v-model="guest.address.address"
                           :input-rows="2"
                           :disabled="!meldeblatt.editable"
                           :label="$t('pageMeldeblatt.address')"
                           name="address"
                           cols="12 6@tablet 3@desktop 3@hd" />
              <hm-input-zip-city :zip="guest.address.zipCode"
                                 :city="guest.address.city"
                                 :country="guest.address.country"
                                 :name-zip="`meldeblatt.meldeblatt.guests.\$each[${slotProps.baseIndex + idx}].addressZipCode`"
                                 :name-city="`meldeblatt.meldeblatt.guests.\$each[${slotProps.baseIndex + idx}].addressCity`"
                                 :parent-errors="errors"
                                 :disabled="!meldeblatt.editable"
                                 cols="12 12@tablet 6@desktop 6@hd"
                                 cols-zip="12 6@tablet 6@desktop 6@hd"
                                 cols-city="12 6@tablet 6@desktop 6@hd"
                                 @update:zip="guest.address.zipCode = $event"
                                 @update:city="guest.address.city = $event"
                                 @update:country="guest.address.country = $event"/>
              <hm-select v-model="guest.address.country"
                         :options="getI18nCountries($shortLocale, [guest.address.country, guest.nationality, ...meldeblatt.meldeblatt.guests.map(it => it.nationality), ...meldeblatt.meldeblatt.guests.map(it => isNil(it.address) ? null : it.address.country)])"
                         :disabled="!meldeblatt.editable"
                         :label="$t('pageMeldeblatt.country')"
                         :parent-errors="errors"
                         :name="`meldeblatt.meldeblatt.guests.\$each[${slotProps.baseIndex + idx}].addressCountry`"
                         cols="12 6@tablet 3@desktop 3@hd" />
            </hm-card-inner>
            <hm-card-inner v-if="['AVS'].includes(config.typ) && (idx >= 1 || slotProps.pageNumber > 1)"
                           title="AVS Abweichender Reisezeitraum">
              <hm-input-date v-model="guest.anreise"
                             :min-date="meldeblatt.meldeblatt.anreise"
                             :max-date="meldeblatt.meldeblatt.abreise"
                             :parent-errors="errors"
                             :min-date-inclusive="true"
                             :required="false"
                             :disabled="!meldeblatt.editable"
                             :label="$t('pageMeldeblatt.arrivalDate')"
                             :name="`meldeblatt.meldeblatt.guests.\$each[${slotProps.baseIndex + idx}].anreise`"
                             cols="12 6@tablet 3@desktop 3@hd start-1" />
              <hm-input-date v-model="guest.abreise"
                             :min-date="meldeblatt.meldeblatt.anreise"
                             :max-date="meldeblatt.meldeblatt.abreise"
                             :parent-errors="errors"
                             :min-date-inclusive="true"
                             :required="false"
                             :disabled="!meldeblatt.editable"
                             :label="$t('pageMeldeblatt.departureDate')"
                             :name="`meldeblatt.meldeblatt.guests.\$each[${slotProps.baseIndex + idx}].abreise`"
                             cols="12 6@tablet 3@desktop 3@hd" />
            </hm-card-inner>
            <hm-card-inner v-if="['AVS'].includes(config.typ) && !isEmpty(config.customFieldsGuest) && idx === 0 && slotProps.pageNumber === 1"
                           title="AVS-Zusatzfelder (falls vorhanden)">
              <template v-for="customField in config.customFieldsGuest">
                <template v-if="customField.displayAlways || customField.displayGastart.includes(`${guest.gastart}`)">
                  <template v-if="customField.type === 'String'">
                    <hm-input v-model="guest.additionalFields[customField.fieldName]"
                              :key="customField.fieldName"
                              :label="customField.label"
                              :maxlength="customField.maxLength"
                              :parent-errors="errors"
                              :disabled="!meldeblatt.editable"
                              :name="`meldeblatt.meldeblatt.guests[${slotProps.baseIndex + idx}].additionalFields.${customField.fieldName}`"
                              cols="12 6@tablet 3@desktop 3@hd"
                    />
                  </template>
                  <template v-if="customField.type === 'Date'">
                    <hm-input-date v-model="guest.additionalFields[customField.fieldName]"
                                   :key="customField.fieldName"
                                   :label="customField.label"
                                   :parent-errors="errors"
                                   :disabled="!meldeblatt.editable"
                                   :name="`meldeblatt.meldeblatt.guests[${slotProps.baseIndex + idx}].additionalFields.${customField.fieldName}`"
                                   :required="false"
                                   cols="12 6@tablet 3@desktop 3@hd"
                    />
                  </template>
                  <template v-if="customField.type === 'Number'">
                    <hm-input-number v-model="guest.additionalFields[customField.fieldName]"
                                     :key="customField.fieldName"
                                     :label="customField.label"
                                     :max-fraction-digits="0"
                                     :min-fraction-digits="0"
                                     :parent-errors="errors"
                                     :disabled="!meldeblatt.editable"
                                     :name="`meldeblatt.meldeblatt.guests[${slotProps.baseIndex + idx}].additionalFields.${customField.fieldName}`"
                                     cols="12 6@tablet 3@desktop 3@hd"
                    />
                  </template>
                  <template v-if="customField.type === 'Dropdown'">
                    <hm-input-select v-model="guest.additionalFields[customField.fieldName]"
                                     :key="customField.fieldName"
                                     :label="customField.label"
                                     :parent-errors="errors"
                                     :disabled="!meldeblatt.editable"
                                     :name="`meldeblatt.meldeblatt.guests[${slotProps.baseIndex + idx}].additionalFields.${customField.fieldName}`"
                                     cols="12 6@tablet 3@desktop 3@hd">
                      <option v-for="it in customField.valueList" :value="it">{{ it }}</option>
                    </hm-input-select>
                  </template>
                </template>
              </template>
              <!--              <hm-col cols="12">-->
              <!--                <pre v-html="guest.additionalFields" />-->
              <!--                <pre v-html="config.customFieldsGuest" />-->
              <!--              </hm-col>-->
            </hm-card-inner>
          </card>
        </template>
      </template>
    </pagination>

    <paginated-table v-if="!isEmpty(meldeblattTransfers)"
                     :id="$attrs['id']"
                     :hover="false"
                     :rows="meldeblattTransfers"
                     :items-per-page="7"
                     columns="4"
                     min-width="500px">
      <template slot="table-head">
        <div class="hm-table__th">{{ $t('pageMeldeblatt.meldeblattTransfers.dateTime') }}</div>
        <div class="hm-table__th">{{ $t('pageMeldeblatt.meldeblattTransfers.username') }}</div>
        <div class="hm-table__th">{{ $t('pageMeldeblatt.meldeblattTransfers.newStatus') }}</div>
        <div class="hm-table__th">{{ $t('pageMeldeblatt.meldeblattTransfers.messageStatus') }}</div>
      </template>
      <template v-slot:default="slotProps">
        <template v-for="it in slotProps.currentPage">
          <div class="hm-table__td">{{ it.dateTime | toDateTime }}</div>
          <div class="hm-table__td">{{ it.username }}</div>
          <div class="hm-table__td">{{ it.meldeStatus }}</div>
          <div class="hm-table__td">{{ it.messageStatus }}</div>
        </template>
      </template>
    </paginated-table>

    <div ref="formHolder" class="hidden"></div>

    <hm-floating-buttons>
      <hm-floating-button :label="$t('pageMeldeblatt.actions.more')" icon="fill-more" color="gray">
        <hm-floating-child-button :label="$t('pageMeldeblatt.actions.cloneMeldeblatt')"
                                  icon="copy"
                                  color="orange"
                                  @click="cloneMeldeblatt" />
        <hm-floating-child-button v-if="meldeblatt.meldeblatt.status === 'Draft'"
                                  :label="$t('pageMeldeblatt.actions.deleteMeldeblatt')"
                                  icon="close"
                                  color="red"
                                  @click="deleteMeldeblatt" />
        <hm-floating-child-button v-if="meldeblatt.meldeblatt.status !== 'Draft' && config.allowStorno === true"
                                  :label="$t('pageMeldeblatt.actions.cancelMeldeblatt')"
                                  icon="close"
                                  color="red"
                                  @click="cancel" />
        <hm-floating-child-button v-if="['Deskline3VTPMS'].includes(config.typ) && (meldeblatt.meldeblatt.status === 'Draft' || meldeblatt.meldeblatt.status === 'Reservation')"
                                  :label="$t('pageMeldeblatt.actions.preRegisterMeldeblatt')"
                                  icon="checkin"
                                  color="green"
                                  @click="preRegistration" />
        <hm-floating-child-button v-if="meldeblatt.editable"
                                  :label="$t('pageMeldeblatt.actions.refreshWithReservationData')"
                                  icon="refresh"
                                  color="orange"
                                  @click="updateFromReservations(false)" />
      </hm-floating-button>
      <hm-floating-button :label="$t('pageMeldeblatt.actions.moreAdd')" icon="printer" color="orange">
        <template v-if="config.hasGuestCard === true && !isEmpty(meldeblatt.printGuestCardLink)">
          <hm-floating-child-button v-if="(['Sent', 'Printed', 'Departed'].includes(meldeblatt.meldeblatt.status)) && ['FeratelV2','FeratelV5'].includes(config.typ)"
                                    :label="$t('pageMeldeblatt.actions.printGuestCard')"
                                    icon="printer"
                                    color="orange"
                                    @click="openLinkAsPost(meldeblatt.printGuestCardLink)" />
          <hm-floating-child-button v-if="(['Reservation', 'Sent', 'Printed', 'Departed'].includes(meldeblatt.meldeblatt.status)) && ['Deskline3VTPMS'].includes(config.typ)"
                                    :label="$t('pageMeldeblatt.actions.printGuestCard')"
                                    icon="printer"
                                    color="orange"
                                    @click="openLink(meldeblatt.printGuestCardLink)" />
        </template>
        <hm-floating-child-button v-if="isEmpty(meldeblatt.printPreviewLink) && !['Sent', 'Printed', 'Departed'].includes(meldeblatt.meldeblatt.status)"
                                  :label="$t('pageMeldeblatt.actions.printPreview')"
                                  icon="printer"
                                  color="orange"
                                  @click="printMeldeblattHotelmeister(meldeblatt.id)" />
        <hm-floating-child-button v-else-if="!['AVS'].includes(config.typ) && isEmpty(meldeblatt.printLink) && ['Sent', 'Printed', 'Departed'].includes(meldeblatt.meldeblatt.status)"
                                  :label="$t('pageMeldeblatt.actions.printMeldeblatt')"
                                  icon="printer"
                                  color="orange"
                                  @click="printMeldeblattHotelmeister(meldeblatt.id)" />
        <template v-else-if="!isEmpty(meldeblatt.printPreviewLink) && !['Sent', 'Printed', 'Departed'].includes(meldeblatt.meldeblatt.status)">
          <hm-floating-child-button v-if="['FeratelV2','FeratelV5'].includes(config.typ)"
                                    :label="$t('pageMeldeblatt.actions.printPreview')"
                                    icon="printer"
                                    color="orange"
                                    @click="openLinkAsPost(meldeblatt.printPreviewLink)" />
          <template v-if="['Deskline3VTPMS'].includes(config.typ)">
            <hm-floating-child-button :label="$t('pageMeldeblatt.actions.printPreviewHotelmeister')" icon="printer" @click="printMeldeblattHotelmeister(meldeblatt.id)" />
            <hm-floating-child-button v-if="!isEmpty(meldeblatt.printBlankLink)"
                                      :label="$t('pageMeldeblatt.actions.printEmptyPreviewFeratel')"
                                      icon="printer"
                                      color="orange"
                                      @click="openLink(meldeblatt.printBlankLink)" />
            <hm-floating-child-button :label="$t('pageMeldeblatt.actions.printPreviewFeratel')" icon="printer" color="orange" @click="preRegistration(() => openLink(meldeblatt.printPreviewLink))" />
          </template>
        </template>
        <template v-else-if="!isEmpty(meldeblatt.printLink) && ['Sent', 'Printed', 'Departed'].includes(meldeblatt.meldeblatt.status)">
          <hm-floating-child-button v-if="['FeratelV2','FeratelV5'].includes(config.typ)"
                                    :label="$t('pageMeldeblatt.actions.printMeldeblatt')"
                                    icon="printer"
                                    color="orange"
                                    @click="openLinkAsPost(meldeblatt.printLink)" />
          <hm-floating-child-button v-if="['Deskline3VTPMS'].includes(config.typ)"
                                    :label="$t('pageMeldeblatt.actions.printMeldeblatt')"
                                    icon="printer"
                                    color="orange"
                                    @click="openLink(meldeblatt.printLink)" />
        </template>
        <template v-if="['AVS'].includes(config.typ) && meldeblatt.meldeblatt.meldeblattNummer > 0">
          <hm-floating-child-button :label="$t('pageMeldeblatt.actions.printMeldeblatt')"
                                    icon="printer"
                                    color="orange"
                                    @click="print(meldeblatt.id)" />
        </template>
      </hm-floating-button>
      <template v-if="hasLicense('Kiosk') && meldeblatt.editable">
        <hm-floating-button v-if="isEmpty(kioskConfigs) || kioskConfigs.length === 1"
                            :throttle="250"
                            :label="$t('pageMeldeblatt.actions.sendToKiosk')"
                            icon="pen-tablet"
                            color="blue"
                            @click="sendToKiosk(null)" />
        <hm-floating-button v-else
                            :label="$t('pageMeldeblatt.actions.sendToKiosk')"
                            icon="pen-tablet"
                            color="blue">
          <hm-floating-child-button v-for="kioskConfig in kioskConfigs"
                                    :label="kioskConfig.name"
                                    :key="kioskConfig.id"
                                    :throttle="250"
                                    icon="pen-tablet"
                                    color="blue"
                                    @click="sendToKiosk(kioskConfig.id)" />
        </hm-floating-button>
      </template>
      <hm-floating-button v-if="meldeblatt.editable"
                          :throttle="250"
                          :label="$t('pageMeldeblatt.actions.addGuest')"
                          icon="plus"
                          color="blue"
                          @click="addGuest" />
      <hm-floating-button v-if="meldeblatt.meldeblatt.meldeblattNummer > 0 && ['AVS'].includes(config.typ)"
                          :throttle="7500"
                          :label="$t('pageMeldeblatt.actions.retrieve')"
                          icon="download"
                          color="orange"
                          @click="retrieve" />
      <hm-floating-button v-if="['Draft', 'Reservation'].includes(meldeblatt.meldeblatt.status)"
                          :throttle="7500"
                          :label="$t('pageMeldeblatt.actions.arrival')"
                          icon="checkin"
                          color="green"
                          @click="registration" />
      <hm-floating-button v-else-if="['Sent', 'Printed'].includes(meldeblatt.meldeblatt.status) && ['CUeGBv1','AVS', 'Deskline3VTPMS'].includes(config.typ)"
                          :throttle="7500"
                          :label="$t('pageMeldeblatt.actions.resend')"
                          icon="upload"
                          color="orange"
                          @click="registration" />
      <hm-floating-button v-if="['Sent', 'Printed'].includes(meldeblatt.meldeblatt.status)"
                          :throttle="7500"
                          :label="$t('pageMeldeblatt.actions.departure')"
                          icon="checkout"
                          color="green"
                          @click="depart" />
    </hm-floating-buttons>
  </main>
</template>


<script>
/* eslint-disable no-cond-assign,no-useless-escape */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import {
  isArray,
  join,
  isNil,
  isEmpty,
  isFunction,
  groupBy,
  values,
  head,
  cloneDeep,
  concat,
  unionBy,
  reverse,
  fromPairs,
  assign,
  toPairs,
  sum,
  size, maxBy, first,
} from 'lodash';
import HmCardInnerLine from '../lib/layout/HmCardInnerLine';
import HmCardInner from '../lib/layout/HmCardInner';
import uuid from 'uuid';
import HmCol from '../lib/layout/HmCol';
import HmInput from '../lib/inputs/HmInput';
import ContenteditableDiv from '../lib/util/ContenteditableDiv';
import NotificationMixin from '../../mixins/NotificationMixin';
import HmTable from '../lib/table/HmTable';
import TabableCard from '../lib/layout/TabableCard';
import TabableCardContent from '../lib/layout/TabableCardContent';
import SaveWatchMixin from '../../mixins/SaveWatchMixin';
import { required, requiredIf, helpers } from 'vuelidate/lib/validators';
import ValidateMixin from '../../mixins/ValidateMixin';
import TimerMixin from '../../mixins/TimerMixin';
import ConcurrentEditMixin from '../../mixins/ConcurrentEditMixin';
import FilterMixin from '../../mixins/FilterMixin';
import HmInputDate from '../lib/inputs/HmInputDate.vue';
import HmInputNumber from '../lib/inputs/HmInputNumber.vue';
import HmInputSelect from '../lib/inputs/HmInputSelect.vue';

export default {
  name: 'PageMeldeblatt',
  components: { HmInputSelect, HmInputNumber, HmInputDate, TabableCardContent, TabableCard, HmTable, ContenteditableDiv, HmCardInner, HmInput, HmCol },

  mixins: [HotelmeisterMixin, SaveWatchMixin, ValidateMixin, TimerMixin, ConcurrentEditMixin, FilterMixin],

  autosave: [
    {
      name: 'meldeblatt',
      variable: 'meldeblatt',
      include: ['meldeblatt'],
      exclude: ['meldeblatt.status', 'meldeblatt.feratelPreCheckInTimestamp', 'meldeblatt.feratelPreCheckInXml', 'meldeblatt.feratelSignature', 'meldeblatt.abgereist', 'meldeblatt.meldeblattNummer'],
      fn: function (cbSaveComplete) {
        const meldeblatt = cloneDeep(this.meldeblatt.meldeblatt);

        if (meldeblatt.status === 'Deleted' || meldeblatt.status === 'Departed') {
          cbSaveComplete();
          return;
        }

        meldeblatt.userInputLandschl = fromPairs(meldeblatt.userInputLandschl);
        this.axios.put(this.serviceUrls.meldeblatt.get(this, this.meldeblattId), meldeblatt)
          .then((response) => {
            this.showMessages(response);
            this.updateWarnings();
            cbSaveComplete();
          })
          .catch((error) => {
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  data() {
    return {
      meldeblatt: null,
      meldeblattId: null,
    };
  },

  computed: {
    dateOfBirthEditable() {
      return this.gastartEditable;
    },
    genderEditable() {
      return this.gastartEditable;
    },
    zusatztextEditable() {
      return this.gastartEditable;
    },
    gastartEditable() {
      if (isEmpty(this.meldeblatt) || isEmpty(this.meldeblatt.meldeblatt) || isEmpty(this.meldeblatt.meldeblatt.guests)) return [];
      return this.meldeblatt.meldeblatt.guests.map((it, idx) => {
        if (isEmpty(this.config)) return false;
        if (['Deskline3VTPMS'].includes(this.config.typ)) {
          // if (this.meldeblatt.editable) {
          //   return true;
          // } else if (['Sent', 'Printed'].includes(this.meldeblatt.meldeblatt.status)) {
          //   if (idx === 0) return false;
          //   else return true;
          //   // return true;
          // } else {
          //   return false;
          // }
          return this.meldeblatt.editable;
        } else {
          return this.meldeblatt.editable;
        }
      });
    },
    exclusions() {
      if (isEmpty(this.meldeblatt)) return [];

      return this.meldeblatt.meldeblatt.guests.flatMap((it, idx) => [
        `meldeblatt.meldeblatt.guests.\$each.${idx}.gastart.custom`,
        `meldeblatt.meldeblatt.guests.\$each.$iter.${idx}.gastart.custom`,
      ]);
    },
    openItems() {
      return [
        {
          className: 'MeldeblattHolder',
          id: this.meldeblattId,
          label: `${this.$t('pageMeldeblatt.title')} #${this.$options.filters.id2number(this.meldeblattId)}`,
        },
      ];
    },
    meldeblattTransfers() {
      if (isEmpty(this.meldeblatt) || isEmpty(this.meldeblatt.meldeblattTransfers)) return [];

      return reverse(this.meldeblatt.meldeblattTransfers);
    },
    kioskConfigs() {
      if (isNil(this.hotelSettings) || isNil(this.hotelSettings.interfaces) || isNil(this.hotelSettings.interfaces.kiosk)) return [];
      return Object.values(this.hotelSettings.interfaces.kiosk);
    },
    config() {
      if (isNil(this.meldeblatt) || isNil(this.meldeblatt.meldeblatt) || isEmpty(this.meldeblatt.meldeblatt.configurationId)) return null;
      if (isNil(this.hotelSettings) || isNil(this.hotelSettings.interfaces) || isNil(this.hotelSettings.interfaces.meldeclient)) return null;
      return this.hotelSettings.interfaces.meldeclient[this.meldeblatt.meldeblatt.configurationId];
    },
    configurations() {
      if (isNil(this.hotelSettings) || isNil(this.hotelSettings.interfaces) || isNil(this.hotelSettings.interfaces.meldeclient)) return [];
      return Object.values(this.hotelSettings.interfaces.meldeclient);
    },
    today() {
      return this.$moment().format('YYYYMMDD');
    },
  },

  watch: {
    'meldeblatt.meldeblatt.configurationId': function (newVal, oldVal) {
      if (isNil(oldVal)) return;
      if (!isEmpty(this.meldeblatt) && !isEmpty(this.meldeblatt.meldeblatt) && !isEmpty(this.meldeblatt.meldeblatt.guests)) {
        this.meldeblatt.meldeblatt.guests.forEach((it, idx) => {
          this.updateGastart(it, idx);
        });
      }
    },
    'meldeblatt.meldeblatt.typ': function (newVal) {
      if ('AVS'.includes(this.config.typ) && newVal !== 'Family') {
        this.meldeblatt.meldeblatt.guests = [this.meldeblatt.meldeblatt.guests[0]];
      }
    },
  },

  created: function () {
    this.meldeblattId = this.$route.params.meldeblattId;
    const self = this;
    // this.$validator.extend('userInputCountsMatch', {
    //   getMessage(field, val) {
    //     return 'Anzahl der Gäste nach Gastart und Herkunftsland muss übereinstimmen.';
    //   },
    //   validate(value, field) {
    //     const gastartCount = sum(Object.values(self.meldeblatt.meldeblatt.userInputGastart));
    //     const landschlCount = sum(self.meldeblatt.meldeblatt.userInputLandschl.map(it => it[1]));
    //
    //     return gastartCount === landschlCount;
    //   },
    // });

    this.updateMeldeblatt();
  },


  validations: function () {
    if (isNil(this.meldeblatt)) return {};

    const ret = {
      numGastart: {
        custom: helpers.withParams(
          { message: this.$t('pageMeldeblatt.validation.gastartAndCountryCountMismatch') },
          () => {
            if (['AVS'].includes(this.config.typ)) {
              return true;
            } else {
              const gastartCount = isEmpty(this.meldeblatt.meldeblatt.userInputGastart) ? 0 : sum(Object.values(this.meldeblatt.meldeblatt.userInputGastart));
              const landschlCount = isEmpty(this.meldeblatt.meldeblatt.userInputLandschl) ? 0 : sum(this.meldeblatt.meldeblatt.userInputLandschl.map(it => it[1]));
              return gastartCount === landschlCount;
            }
          },
        ),
      },
      meldeblatt: {
        meldeblatt: {
          guests: {
            0: {
              travelDocumentNumber: {
                custom: helpers.withParams(
                  { message: this.$t('pageMeldeblatt.validation.travelDocumentRequiredForeigners') },
                  (_, guest) => {
                    if (guest.sameAddressAsPrimary === true) {
                      return true;
                    }
                    if (['AT', 'DE'].includes(this.hotelSettings.details.countryCode)) {
                      if ((guest.nationality || guest.address.country) === this.hotelSettings.details.countryCode) {
                        return true;
                      } else {
                        return !isEmpty(guest.document.number);
                      }
                    } else {
                      return true;
                    }
                  },
                ),
              },
              travelDocumentType: {
                custom: helpers.withParams(
                  { message: this.$t('pageMeldeblatt.validation.travelDocumentRequiredForeigners') },
                  (_, guest) => {
                    if (guest.sameAddressAsPrimary === true) {
                      return true;
                    }
                    if (['AT', 'DE'].includes(this.hotelSettings.details.countryCode)) {
                      if ((guest.nationality || guest.address.country) === this.hotelSettings.details.countryCode) {
                        return true;
                      } else {
                        return !isEmpty(guest.document.type);
                      }
                    } else {
                      return true;
                    }
                  },
                ),
              },
              additionalFields:
                (['AVS'].includes(this.config.typ) && !isEmpty(this.config) && !isEmpty(this.config.customFieldsGuest))
                  ? Object.fromEntries(
                    this.config.customFieldsGuest
                      .filter(it => it.displayAlways || it.displayGastart.includes(this.meldeblatt.meldeblatt.guests[0].gastart))
                      .filter(it => !isEmpty(it.requiredGastart))
                      .map(it => [
                        it.fieldName,
                        {
                          required_if: requiredIf(_ => it.requiredGastart.includes(this.meldeblatt.meldeblatt.guests[0].gastart)),
                          label: helpers.withParams(
                            { label: it.label },
                            () => true,
                          ),
                        },
                      ]),
                  )
                  : {},
            },
            $each: {
              lastName: {
                required,
                label: helpers.withParams(
                  { label: this.$t('pageMeldeblatt.lastName') },
                  () => true,
                ),
              },
              gastart: {
                required,
                custom: helpers.withParams(
                  { message: this.$t('pageMeldeblatt.validation.gastartAgeMismatch') },
                  (gastart, guest) => gastart === this.getGastart(guest, -1),
                ),
                label: helpers.withParams(
                  { label: this.$t('pageMeldeblatt.gastart') },
                  () => true,
                ),
              },
              dateOfBirth: {
                ...this.$hv__createValidator('birthdate'),
                required: requiredIf(_ => this.config.requireBirthDate),
                label: helpers.withParams(
                  { label: this.$t('pageMeldeblatt.dateOfBirth') },
                  () => true,
                ),
              },
              addressCountry: {
                custom: helpers.withParams(
                  { message_key: 'required' },
                  (_, guest) => {
                    if (guest.sameAddressAsPrimary === true) return true;
                    else return !isEmpty(guest.address.country);
                  },
                ),
                label: helpers.withParams(
                  { label: this.$t('pageMeldeblatt.country') },
                  () => true,
                ),
              },
              anreise: {
                custom: helpers.withParams(
                  { message_key: 'invalid' },
                  (val, guest) => {
                    if (!['AVS'].includes(this.config.typ)) return true;
                    if (isEmpty(val)) return true;
                    if (val < this.meldeblatt.meldeblatt.anreise) return false;
                    if (val > this.meldeblatt.meldeblatt.abreise) return false;
                    if (val >= guest.abreise && !isEmpty(guest.abreise)) return false;
                    return true;
                  },
                ),
                label: helpers.withParams(
                  { label: this.$t('pageMeldeblatt.arrivalDate') },
                  () => true,
                ),
              },
              abreise: {
                custom: helpers.withParams(
                  { message_key: 'invalid' },
                  (val, guest) => {
                    if (!['AVS'].includes(this.config.typ)) return true;
                    if (isEmpty(val)) return true;
                    if (val < this.meldeblatt.meldeblatt.anreise) return false;
                    if (val > this.meldeblatt.meldeblatt.abreise) return false;
                    if (val <= guest.anreise && !isEmpty(guest.anreise)) return false;
                    return true;
                  },
                ),
                label: helpers.withParams(
                  { label: this.$t('pageMeldeblatt.departureDate') },
                  () => true,
                ),
              },
              addressZipCode: {
                customRequired: helpers.withParams(
                  { message: this.$t('pageMeldeblatt.validation.zipCodeRequiredForCountry') },
                  (_, guest) => {
                    if (guest.sameAddressAsPrimary === true) {
                      return true;
                    } else if (this.hotelSettings.details.countryCode === 'AT') {
                      if (['AT', 'DE', 'CH'].includes(guest.address.country) && isEmpty(guest.address.zipCode)) return false;
                      else return true;
                    } else {
                      return true;
                    }
                  },
                ),
                customValidZipCode: helpers.withParams(
                  { message: this.$t('pageMeldeblatt.validation.zipCodeInvalidForCountry') },
                  (_, guest) => {
                    if (guest.sameAddressAsPrimary === true) {
                      return true;
                    } else if (this.hotelSettings.details.countryCode === 'AT') {
                      switch (guest.address.country) {
                        case 'AT': return size(guest.address.zipCode) === 4;
                        case 'DE': return size(guest.address.zipCode) === 5;
                        case 'CH': return size(guest.address.zipCode) === 4;
                        default:
                          return true;
                      }
                    } else {
                      return true;
                    }
                  },
                ),
              },
            },
          },
        },
      },
    };

    return ret;
  },

  methods: {
    isEmpty,
    printMeldeblattHotelmeister(meldeblattId) {
      this.printHTML(
        window.getOldUIPath(
          this.hotelSettings.tenant.subdomain,
          this.hotelSettings.hotel.id,
          `/rest/ui/backend/hotel/${this.$route.params.hotelId}/print/meldeblatt_preview?meldeblattId=${meldeblattId}`,
          window.getToken(this.$route.params.hotelId),
          null,
          true,
        ),
      );
    },
    printHTML(url) {
      this.$serviceBus.$emit('printHTML', url);
    },
    openLink(url) {
      const newWindow = window.open(url, 'meldeblatt');
      if (!isNil(newWindow)) newWindow.focus();
    },
    openLinkAsPost(myurl) {
      function matchBaseUrl(url) {
        const regex = /^[^&\?]+/g;
        let m = null;

        if ((m = regex.exec(url)) !== null) {
          // This is necessary to avoid infinite loops with zero-width matches
          if (m.index === regex.lastIndex) {
            regex.lastIndex++;
          }

          return m[0];
        }

        return '';
      }

      function matchVariables(url) {
        const regex = /[\?&]([^=]+)=([^\?&\n]+)/g;

        const ret = [];
        let m;

        while ((m = regex.exec(url)) !== null) {
          // This is necessary to avoid infinite loops with zero-width matches
          if (m.index === regex.lastIndex) {
            regex.lastIndex++;
          }

          ret.push([m[1], m[2]]);
        }

        return ret;
      }


      const baseUrl = matchBaseUrl(myurl);
      const args = matchVariables(myurl);

      let form = `<form id="form_print_link" action="${baseUrl}" method="POST" target="_blank">`;
      args.forEach((entry) => {
        form += `<input type="hidden" name="${entry[0]}" value="${entry[1]}" />`;
      });
      form += '</form>';

      this.$refs.formHolder.innerHTML = form;
      document.getElementById('form_print_link').submit();
    },
    updateUserInputValidations: function () {
      this.$v.numGastart.$touch();
      // if (isNil(this.$refs.userInputRefs)) return;

      // this.$refs.userInputRefs.forEach((it) => {
      //   // it.$validator.validateAll();
      // });
    },
    updateFromReservations: function (force = false) {
      if (force === false) {
        this.showModal(
          this.$t('pageMeldeblatt.confirmUpdateFromReservationOverwriteExistingData'),
          () => {
            this.updateFromReservations(true);
          },
        );
      }

      if (force === true) {
        this.axios.post(this.serviceUrls.meldeblatt.updateFromReservations(this, this.meldeblattId))
          .then((response) => {
            this.showMessages(response);
            this.updateMeldeblatt().then(() => this.showSuccessMessage());
          })
          .catch((error) => {
            this.showMessages(error.response);
            if (!isNil(this.meldeblatt) && !isNil(error.response) && !isNil(error.response.data) && !isNil(error.response.data.data)) this.meldeblatt.meldeblattTransfers = error.response.data.data.meldeblattTransfers;
          });
      }
    },
    cancel: function () {
      this.axios.post(this.serviceUrls.meldeblatt.cancel(this, this.meldeblattId))
        .then((response) => {
          this.showMessages(response);
          this.updateMeldeblatt().then(() => this.showSuccessMessage());
        })
        .catch((error) => {
          this.showMessages(error.response);
          if (!isNil(this.meldeblatt) && !isNil(error.response) && !isNil(error.response.data) && !isNil(error.response.data.data)) this.meldeblatt.meldeblattTransfers = error.response.data.data.meldeblattTransfers;
        });
    },
    deleteMeldeblatt: function () {
      this.axios.delete(this.serviceUrls.meldeblatt.delete(this, this.meldeblattId))
        .then((response) => {
          this.showMessages(response);
          this.updateMeldeblatt().then(() => this.showSuccessMessage());
        })
        .catch((error) => {
          this.showMessages(error.response);
          if (!isNil(this.meldeblatt) && !isNil(error.response) && !isNil(error.response.data) && !isNil(error.response.data.data)) this.meldeblatt.meldeblattTransfers = error.response.data.data.meldeblattTransfers;
        });
    },
    cloneMeldeblatt: function () {
      this.axios.post(this.serviceUrls.meldeblatt.clone(this, this.meldeblattId))
        .then((response) => {
          this.showMessages(response);
          this.showSuccessMessage();
          const meldeblattId = response.data.data.id;

          if (!isEmpty(meldeblattId)) {
            this.$router.push({
              name: 'meldeblatt',
              params: {
                meldeblattId: meldeblattId,
              },
            });
          }
        })
        .catch((error) => {
          this.showMessages(error.response);
        });
    },
    depart: function () {
      this.axios.post(this.serviceUrls.meldeblatt.depart(this, this.meldeblattId))
        .then((response) => {
          this.showMessages(response);
          this.__pauseAutosave('meldeblatt'); // pause, so we don't try to save the updated data once again
          this.updateMeldeblatt().then(() => this.showSuccessMessage());
        })
        .catch((error) => {
          this.showMessages(error.response);
          if (!isNil(this.meldeblatt) && !isNil(error.response) && !isNil(error.response.data) && !isNil(error.response.data.data)) this.meldeblatt.meldeblattTransfers = error.response.data.data.meldeblattTransfers;
        });
    },
    validate: function (exclusions = [''], showMessage = true) {
      try { this.meldeblatt.meldeblatt.guests[0].sameAddressAsPrimary = false; } catch (ignore) { /* ignore */ }
      this.$v.$touch();
      if (showMessage && this.$v.$anyError && !isEmpty(this.failedValidations.filter(it => !(exclusions || []).includes(it)))) {
        this.showErrorMessage(this.$t('pageMeldeblatt.validationFailed'));

        const firstFailedGuest = head(Object.entries(this.$v.meldeblatt.meldeblatt.guests.$each.$iter).filter(it => it[1].$anyError));
        if (!isEmpty(firstFailedGuest)) {
          const failedGuestIdx = parseInt(firstFailedGuest[0], 10);
          const failedGuestPage = Math.floor(failedGuestIdx / 6);

          const guestPagination = this.$refs.guestPagination;
          if (!isEmpty(guestPagination)) {
            guestPagination.pageNumber = failedGuestPage + 1;
          }
        } else if (this.$v.meldeblatt.meldeblatt.guests[0].$anyError) {
          const guestPagination = this.$refs.guestPagination;
          if (!isEmpty(guestPagination)) {
            guestPagination.pageNumber = 1;
          }
        }

        // const idx = parseInt(it[0], 10);
        // const val = it[1];
        // this.$v.meldeblatt.meldeblatt.guests.$each.forEach((it) => {
        //   console.log(it);
        // });

        this.$nextTick(() => {
          const errorElement = document.querySelector('.hasError');
          if (!isEmpty(errorElement)) {
            if (isFunction(errorElement.scrollIntoViewIfNeeded)) errorElement.scrollIntoViewIfNeeded();
            else errorElement.scrollIntoView();
          }
        });

        return false;
      }

      return true;
    },
    registration: function () {
      if (!this.validate(this.exclusions)) return;

      this.axios.post(this.serviceUrls.meldeblatt.registration(this, this.meldeblattId))
        .then((response) => {
          this.showMessages(response);
          this.__pauseAutosave('meldeblatt');
          this.updateMeldeblatt().then(() => this.showSuccessMessage()).finally(() => this.__continueAutosave('meldeblatt'));
        })
        .catch((error) => {
          this.showMessages(error.response);
          if (!isNil(this.meldeblatt) && !isNil(error.response) && !isNil(error.response.data) && !isNil(error.response.data.data)) this.meldeblatt.meldeblattTransfers = error.response.data.data.meldeblattTransfers;
        });
    },
    print: function () {
      this.axios.post(this.serviceUrls.meldeblatt.print(this, this.meldeblattId))
        .then((response) => {
          try {
            const windowUrl = URL.createObjectURL(this.b64toBlob(response.data.data, 'application/pdf;charset=utf-8'));
            window.open(windowUrl, '_blank');
          } catch (e) {
            this.showErrorMessage('Invalid file type');
          }
          this.showMessages(response);
          this.__pauseAutosave('meldeblatt');
          this.updateMeldeblatt().then(() => this.showSuccessMessage()).finally(() => this.__continueAutosave('meldeblatt'));
        })
        .catch((error) => {
          this.showMessages(error.response);
        });
    },
    retrieve: function () {
      this.axios.post(this.serviceUrls.meldeblatt.retrieve(this, this.meldeblattId))
        .then((response) => {
          this.showMessages(response);
          this.__pauseAutosave('meldeblatt');
          this.updateMeldeblatt().then(() => this.showSuccessMessage()).finally(() => this.__continueAutosave('meldeblatt'));
        })
        .catch((error) => {
          this.showMessages(error.response);
        });
    },
    b64toBlob: function (b64Data, contentType = '', sliceSize = 512) {
      const byteCharacters = atob(b64Data);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, { type: contentType });
      return blob;
    },
    preRegistration: function (fun = null) {
      if (!this.validate(['meldeblatt.meldeblatt.guests.0.travelDocumentNumber.custom', 'meldeblatt.meldeblatt.guests.0.travelDocumentType.custom'], false)) return;

      this.axios.post(this.serviceUrls.meldeblatt.preRegistration(this, this.meldeblattId))
        .then((response) => {
          this.showMessages(response);
          this.updateMeldeblatt().then(() => {
            if (!isNil(fun) && isFunction(fun)) {
              fun();
            } else {
              // do not show the success message if the preRegistration is just a wrapper around the actual print function
              this.showSuccessMessage();
            }
            if (!isNil(this.meldeblatt)) this.meldeblatt.meldeblattTransfers = response.data.data.meldeblattTransfers;
          });
        })
        .catch((error) => {
          this.showMessages(error.response);
          if (!isNil(this.meldeblatt) && !isNil(error.response) && !isNil(error.response.data) && !isNil(error.response.data.data)) this.meldeblatt.meldeblattTransfers = error.response.data.data.meldeblattTransfers;
        });
    },
    deleteGuest: function (guest) {
      this.meldeblatt.meldeblatt.guests.splice(this.meldeblatt.meldeblatt.guests.indexOf(guest), 1);
      head(this.meldeblatt.meldeblatt.guests).gasttyp = 'HauptgemelderGast';
    },
    addGuest: function () {
      if (this.meldeblatt.meldeblatt.typ === 'Family' && this.meldeblatt.meldeblatt.guests.length >= this.config.maxNumberGuests) {
        this.showErrorMessage(`Maximale Anzahl an Gästen für Meldetyp '${this.$t(`Meldetyp.${this.meldeblatt.meldeblatt.typ}`)}' wurde bereits erreicht.`);
        return;
      } else if (this.meldeblatt.meldeblatt.typ !== 'Family' && this.meldeblatt.meldeblatt.guests.length >= this.config.maxNumberGuestsGroup) {
        this.showErrorMessage(`Maximale Anzahl an Gästen für Meldetyp '${this.$t(`Meldetyp.${this.meldeblatt.meldeblatt.typ}`)}' wurde bereits erreicht.`);
        return;
      }
      this.meldeblatt.meldeblatt.guests.push({
        gastart: isEmpty(this.config.gastarten) ? null : head(this.config.gastarten).code,
        gasttyp: 'MitreisendePerson',
        gender: 'Undeclared',
        address: { id: uuid.v4() },
        email: { id: uuid.v4() },
        phoneNumber: { id: uuid.v4() },
        sameAddressAsPrimary: this.meldeblatt.meldeblatt.typ === 'Family',
        document: { },
        additionalFields: { },
      });
    },
    openReservation: function (reservationId) {
      this.$router.push({ name: 'reservation', params: { reservationId: reservationId } });
    },
    updateMeldeblatt() {
      if (isEmpty(this.configurations)) {
        this.setTimeout(this.updateMeldeblatt, 250);
        return Promise.resolve();
      }

      return this.axios.get(this.serviceUrls.meldeblatt.get(this, this.meldeblattId))
        .then((response) => {
          const meldeblatt = response.data.data;

          const config = this.hotelSettings.interfaces.meldeclient[meldeblatt.meldeblatt.configurationId];

          meldeblatt.meldeblatt.userInputGastart = assign(fromPairs(config.gastarten.map(it => [it.code, 0])), meldeblatt.meldeblatt.userInputGastart);
          meldeblatt.meldeblatt.userInputLandschl = isEmpty(meldeblatt.meldeblatt.userInputLandschl) ? [[this.hotelSettings.details.countryCode, 0]] : toPairs(meldeblatt.meldeblatt.userInputLandschl);

          this.meldeblatt = meldeblatt;

          if (this.meldeblatt.editable) {
            this.__autosave();
            this.$nextTick(() => {
              this.meldeblatt.meldeblatt.guests.forEach((it) => {
                if (isEmpty(it.gastart)) {
                  it.gastart = isEmpty(config.gastarten) ? null : head(config.gastarten).code;
                }
                if (isEmpty(it.gender)) {
                  it.gender = 'Undeclared';
                }
                if (isNil(it.email)) {
                  it.email = { id: uuid.v4() };
                }
              });
            });
          }
        });
    },
    updateWarnings() {
      this.axios.get(this.serviceUrls.meldeblatt.get(this, this.meldeblattId))
        .then((response) => {
          this.meldeblatt.dateMismatch = response.data.data.dateMismatch;
          this.meldeblatt.numPersonsMismatch = response.data.data.numPersonsMismatch;
          this.meldeblatt.anotherMeldeblattAlreadyExists = response.data.data.anotherMeldeblattAlreadyExists;
        });
    },
    sendToKiosk(instanceId = null) {
      this.axios.post(
        this.serviceUrls.meldeblatt.sendToKiosk(this, this.meldeblattId),
        instanceId,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
        .then((response) => {
          this.showSuccessMessage();
        })
        .catch((error) => {
          this.showMessages(error.response);
        });
    },
    deleteSignature() {
      this.axios.delete(this.serviceUrls.meldeblatt.deleteSignature(this, this.meldeblattId))
        .then((response) => {
          this.showMessages(response);
          this.updateMeldeblatt().then(() => this.showSuccessMessage());
        })
        .catch((error) => {
          this.showMessages(error.response);
        });
    },
    updateGastart(guest, idx) {
      const gastart = this.getGastart(guest, idx);

      if (this.gastartEditable[idx]) {
        if (!isEmpty(gastart)) {
          guest.gastart = gastart;
        }
      }
    },
    getGastart(guest, idx = -1) {
      try {
        if (!isNil(this.config) && !isNil(guest.dateOfBirth)) {
          try {
            if (idx < 0) {
              idx = this.meldeblatt.meldeblatt.guests.indexOf(guest);
            }
          } catch (ignore) {
            idx = 0;
          }

          const gastarten = this.config.gastarten.filter(it => idx === 0 || !it.primaryGuestOnly);
          const adultGastart = maxBy(gastarten, 'ageUntil');
          const exactAge = this.config.exactAge;

          let ageEffectiveDate;

          if (exactAge === true) {
            ageEffectiveDate = this.meldeblatt.meldeblatt.anreise;
          } else if (!isNil(this.config.ageEffectiveDate)) {
            let tmpDate = this.$moment(`${this.meldeblatt.meldeblatt.anreise}-${this.config.ageEffectiveDate[0]}-${this.config.ageEffectiveDate[1]}`, 'YYYY-MM-DD');
            const anreise = this.$moment(this.meldeblatt.meldeblatt.anreise, 'YYYYMMDD');
            if (tmpDate.isAfter(anreise)) {
              tmpDate = this.$moment(`${parseInt(this.meldeblatt.meldeblatt.anreise, 10) - 1}-${this.config.ageEffectiveDate[0]}-${this.config.ageEffectiveDate[1]}`, 'YYYY-MM-DD');
            }
            ageEffectiveDate = tmpDate.format('YYYYMMDD');
          } else {
            ageEffectiveDate = `${this.meldeblatt.meldeblatt.anreise.substring(0, 4)}0101`;
          }

          const age = Math.abs(this.$moment(ageEffectiveDate, 'YYYYMMDD').diff(this.$moment(guest.dateOfBirth, 'YYYYMMDD'), 'years'));

          const applicableGastarten = gastarten.filter(it => age >= it.ageFrom && age <= it.ageUntil);
          const gastart = applicableGastarten.find(it => it.code === `${guest.gastart}`) || first(applicableGastarten);

          if (!isNil(gastart)) {
            return gastart.code;
          } else if (!isNil(adultGastart)) {
            return adultGastart.code;
          } else {
            return null;
          }
        }
        return null;
      } catch (e) {
        this.$rollbar.error(e);
        return guest.gastart; // don't suggest any changes in case of an error
      }
    },
    openHTML() {
      this.axios.get(this.serviceUrls.meldeblatt.html(this, this.meldeblattId))
        .then((response) => {
          const content = response.data.data;

          const windowUrl = URL.createObjectURL(
            new Blob([content], { type: 'text/html;charset=utf-8' }),
          );

          window.open(windowUrl, '_blank');
        });
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
  @import '~styles/import';

  .hm-input {
    &__image {
      &-wrapper {
        max-height: 15.5rem;
        width:100%;
        padding: 1rem;
        border-radius:0.3rem;
        border: 1px solid $middlegray;
        margin-bottom: 1rem;
        display: flex;
        flex-direction: row;
        position: relative;
        flex: 1;
        justify-content: left;
      }
      object-fit: scale-down;
      flex: 0 0 auto;
      width: 500px;
      max-width: 100%;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/PageMeldeblatt.vue